import { startOfDay } from 'date-fns';
import { useMemo } from 'react';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { formatDateKey, parseDateKey } from 'shared/utils/format-date-key';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getScheduleCompletedOnDate } from 'shared/utils/get-schedule-completed-on-date';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
};

export const useCompletedDateTree = ({
  tasks,
  customOrder,
  sortingMode,
}: Options): Record<ID, TreeItem> => {
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  return useMemo(() => {
    const occurrences: { taskId: ID; dateKey: string; date: Timestamp }[] = [];

    tasks.forEach((task) => {
      // 1. Regular tasks / one-off completions
      if (task.completedAt) {
        occurrences.push({
          taskId: task.id,
          dateKey: formatDateKey(task.completedAt),
          date: startOfDay(task.completedAt),
        });
        return;
      }

      // 2. Habits and Repeating Tasks (from schedules)
      if (!task.schedules?.length) {
        return;
      }

      const completionDates = new Set<string>();

      // Check all entries for completions
      task.schedules.forEach((schedule) => {
        schedule.entries.forEach((entry) => {
          if (entry.state === ScheduleEntryState.Complete && !!entry.value) {
            completionDates.add(formatDateKey(entry.date));
          }
        });
      });

      // Special check for Today (standard Griply logic for active progress)
      const activeSchedule = getActiveSchedule(task.schedules, today);
      if (activeSchedule && getScheduleCompletedOnDate(activeSchedule, today)) {
        completionDates.add(formatDateKey(today));
      }

      completionDates.forEach((dateKey) => {
        const date = startOfDay(parseDateKey(dateKey));
        occurrences.push({
          taskId: task.id,
          dateKey,
          date,
        });
      });
    });

    const dateSections: Record<string, { date: Timestamp; taskIds: ID[] }> = {};
    occurrences.forEach((occ) => {
      if (!dateSections[occ.dateKey]) {
        dateSections[occ.dateKey] = {
          date: occ.date,
          taskIds: [],
        };
      }
      if (!dateSections[occ.dateKey].taskIds.includes(occ.taskId)) {
        dateSections[occ.dateKey].taskIds.push(occ.taskId);
      }
    });

    const dateTreeItems: Record<ID, TreeItem> = {};

    // Sort date sections chronologically reversed (latest first)
    const sortedDateKeys = Object.keys(dateSections).sort(
      (a, b) => dateSections[b].date.getTime() - dateSections[a].date.getTime(),
    );

    sortedDateKeys.forEach((dateKey) => {
      dateTreeItems[dateKey] = {
        id: dateKey,
        type: TreeItemTypes.Date,
        name: dateKey,
        children: [],
        isFolder: true,
      };
    });

    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? Object.keys(dateTreeItems).reduce<ID[]>((acc, sectionId) => {
            const sectionOrder = customOrder?.[sectionId] ?? [];
            acc.push(...sectionOrder);
            return acc;
          }, [])
        : [];

    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.CreatedAt, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      Object.keys(dateSections).forEach((dateKey) => {
        if (dateSections[dateKey].taskIds.includes(task.id)) {
          const occurrenceId = `occurrence:${dateKey}:${task.id}`;
          if (acc[dateKey]) {
            acc[dateKey].children?.push(occurrenceId);
          }
          acc[occurrenceId] = {
            id: occurrenceId,
            name: task.name,
            type: TreeItemTypes.Task,
          };
        }
      });

      return acc;
    }, dateTreeItems);

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: sortedDateKeys,
      },
      ...allTreeItems,
    };
  }, [tasks, customOrder, sortingMode, today, weekStartsOn]);
};
